<template>
  <div class="sx-vue-calendar-wrapper">
    <ScheduleXCalendar :calendar-app="calendarApp" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ScheduleXCalendar } from '@schedule-x/vue';
import { createCalendar, createViewMonthGrid, createViewWeek, createViewDay } from '@schedule-x/calendar';
import { createEventsServicePlugin } from '@schedule-x/events-service';
import { createEventModalPlugin } from '@schedule-x/event-modal';
import { mix, darken } from 'polished';
import '@schedule-x/theme-default/dist/index.css';

// Initialize the events service plugin
const eventsServicePlugin = createEventsServicePlugin();

const fetchAcceptedVacations = async () => {
  try {
    const response = await fetch('/wp-json/vacation/v1/accepted-requests');
    if (response.ok) {
      const data = await response.json();

      const uniqueCalendarIds = new Set();
      let dynamicStyles = '';

      // Filter out overtimes (assuming overtimes don't have a start or end date)
      const filteredData = data.filter(event => event.start && event.end);

      const events = filteredData.map(event => {
        const baseColor = event.color || '#000';
        const containerColor = mix(0.85, '#ffffff', baseColor);
        const onContainerColor = darken(0.2, baseColor);

        if (!uniqueCalendarIds.has(event.calendarId)) {
          dynamicStyles += `.custom-event-${event.calendarId} {
            background-color: ${containerColor} !important;
            color: ${onContainerColor} !important;
            border: 1px solid ${containerColor} !important;
            border-left: 4px solid ${baseColor} !important;
          }`;
          uniqueCalendarIds.add(event.calendarId);
        }

        return {
          ...event,
          start: event.start.split('T')[0],
          end: event.end.split('T')[0],
          _options: {
            additionalClasses: [`custom-event-${event.calendarId}`],
          },
        };
      });

      const styleSheet = document.createElement("style");
      styleSheet.innerText = dynamicStyles.trim();
      document.head.appendChild(styleSheet);

      console.log('Processed events:', events);
      return events;
    } else {
      console.error('Failed to fetch events');
      return [];
    }
  } catch (error) {
    console.error('Error fetching events:', error);
    return [];
  }
};

const fetchNationalHolidays = async () => {
  const countryEmojiMap = {
    Ukraine: '🇺🇦',
    Romania: '🇷🇴',
    USA: '🇺🇸',
    'United States': '🇺🇸',
    Scotland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
    Philippines: '🇵🇭',
    Serbia: '🇷🇸',
    Japan: '🇯🇵',
    Germany: '🇩🇪',
    France: '🇫🇷',
    Italy: '🇮🇹',
    Spain: '🇪🇸',
    Canada: '🇨🇦',
    Australia: '🇦🇺',
    Brazil: '🇧🇷',
    Mexico: '🇲🇽',
    India: '🇮🇳',
    China: '🇨🇳',
    'United Kingdom': '🇬🇧',
    UK: '🇬🇧',
    England: '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
    Poland: '🇵🇱',
    Netherlands: '🇳🇱',
    Belgium: '🇧🇪',
    Sweden: '🇸🇪',
    Norway: '🇳🇴',
    Denmark: '🇩🇰',
    Finland: '🇫🇮',
    Portugal: '🇵🇹',
    Greece: '🇬🇷',
    Turkey: '🇹🇷',
    Russia: '🇷🇺',
    'South Korea': '🇰🇷',
    Thailand: '🇹🇭',
    Vietnam: '🇻🇳',
    Singapore: '🇸🇬',
    'New Zealand': '🇳🇿',
    'South Africa': '🇿🇦',
    Egypt: '🇪🇬',
    Israel: '🇮🇱',
    'Saudi Arabia': '🇸🇦',
    Argentina: '🇦🇷',
    Chile: '🇨🇱',
    Colombia: '🇨🇴',
    Venezuela: '🇻🇪',
    Peru: '🇵🇪',
    Ecuador: '🇪🇨',
    Bolivia: '🇧🇴',
    Uruguay: '🇺🇾',
    Paraguay: '🇵🇾',
    Ireland: '🇮🇪',
    Switzerland: '🇨🇭',
    Austria: '🇦🇹',
    Czech: '🇨🇿',
    'Czech Republic': '🇨🇿',
    Slovakia: '🇸🇰',
    Hungary: '🇭🇺',
    Slovenia: '🇸🇮',
    Croatia: '🇭🇷',
    Bosnia: '🇧🇦',
    Montenegro: '🇲🇪',
    Albania: '🇦🇱',
    Macedonia: '🇲🇰',
    Bulgaria: '🇧🇬',
    Moldova: '🇲🇩',
    Lithuania: '🇱🇹',
    Latvia: '🇱🇻',
    Estonia: '🇪🇪',
    Belarus: '🇧🇾',
    Georgia: '🇬🇪',
    Armenia: '🇦🇲',
    Azerbaijan: '🇦🇿',
    Kazakhstan: '🇰🇿',
    Uzbekistan: '🇺🇿',
    Kyrgyzstan: '🇰🇬',
    Tajikistan: '🇹🇯',
    Turkmenistan: '🇹🇲',
    Mongolia: '🇲🇳',
    Afghanistan: '🇦🇫',
    Pakistan: '🇵🇰',
    Bangladesh: '🇧🇩',
    'Sri Lanka': '🇱🇰',
    Myanmar: '🇲🇲',
    Cambodia: '🇰🇭',
    Laos: '🇱🇦',
    Malaysia: '🇲🇾',
    Indonesia: '🇮🇩',
    Brunei: '🇧🇳',
    Taiwan: '🇹🇼',
    'Hong Kong': '🇭🇰',
    Macau: '🇲🇴',
    'North Korea': '🇰🇵',
    Iran: '🇮🇷',
    Iraq: '🇮🇶',
    Syria: '🇸🇾',
    Lebanon: '🇱🇧',
    Jordan: '🇯🇴',
    Kuwait: '🇰🇼',
    Qatar: '🇶🇦',
    UAE: '🇦🇪',
    'United Arab Emirates': '🇦🇪',
    Bahrain: '🇧🇭',
    Oman: '🇴🇲',
    Yemen: '🇾🇪',
    Morocco: '🇲🇦',
    Tunisia: '🇹🇳',
    Algeria: '🇩🇿',
    Libya: '🇱🇾',
    Sudan: '🇸🇩',
    Ethiopia: '🇪🇹',
    Kenya: '🇰🇪',
    Uganda: '🇺🇬',
    Tanzania: '🇹🇿',
    Rwanda: '🇷🇼',
    Burundi: '🇧🇮',
    'Democratic Republic of Congo': '🇨🇩',
    'Central African Republic': '🇨🇫',
    Chad: '🇹🇩',
    Niger: '🇳🇪',
    Nigeria: '🇳🇬',
    Ghana: '🇬🇭',
    'Ivory Coast': '🇨🇮',
    Burkina: '🇧🇫',
    Mali: '🇲🇱',
    Senegal: '🇸🇳',
    Mauritania: '🇲🇷',
    Gambia: '🇬🇲',
    'Guinea-Bissau': '🇬🇼',
    Guinea: '🇬🇳',
    'Sierra Leone': '🇸🇱',
    Liberia: '🇱🇷',
    Togo: '🇹🇬',
    Benin: '🇧🇯',
    Cameroon: '🇨🇲',
    'Equatorial Guinea': '🇬🇶',
    Gabon: '🇬🇦',
    'Republic of Congo': '🇨🇬',
    Angola: '🇦🇴',
    Namibia: '🇳🇦',
    Botswana: '🇧🇼',
    Zambia: '🇿🇲',
    Zimbabwe: '🇿🇼',
    Mozambique: '🇲🇿',
    Madagascar: '🇲🇬',
    Mauritius: '🇲🇺',
    Comoros: '🇰🇲',
    Seychelles: '🇸🇨',
    Djibouti: '🇩🇯',
    Somalia: '🇸🇴',
    Eritrea: '🇪🇷'
  };
  try {
    const response = await fetch('/wp-json/vacation/v1/national-holidays');
    if (response.ok) {
      const data = await response.json();

      return data.map(holiday => {
        // Get emoji for the country, fallback to a generic flag emoji if not found
        const countryEmoji = countryEmojiMap[holiday.country_name] || '🏁';
        const displayText = countryEmojiMap[holiday.country_name] 
          ? `${holiday.holiday_name} ${countryEmoji}` 
          : `${holiday.holiday_name} (${holiday.country_name})`;
          
        return {
          title: displayText,
          start: holiday.date.split('/').reverse().join('-'), // Convert to 'YYYY-MM-DD'
          end: holiday.date.split('/').reverse().join('-'),
          _options: {
            additionalClasses: ['national-holiday'], // Apply custom class for styling
          },
        };
      });
    } else {
      console.error('Failed to fetch national holidays');
      return [];
    }
  } catch (error) {
    console.error('Error fetching national holidays:', error);
    return [];
  }
};

const fetchAcceptedVacationsAndHolidays = async () => {
  const vacations = await fetchAcceptedVacations();
  const holidays = await fetchNationalHolidays();

  const events = [...vacations, ...holidays];

  // Set the combined events
  eventsServicePlugin.set(events);

  console.log('Processed events:', events);
};



const monthView = createViewMonthGrid();
const weekView = createViewWeek();
const dayView = createViewDay();

const calendarApp = createCalendar({
  selectedDate: new Date().toISOString().split('T')[0],
  views: [monthView],
  defaultView: monthView.name,
  plugins: [eventsServicePlugin, createEventModalPlugin()],
  calendars: {}, // Initially empty, will be populated dynamically
});

onMounted(() => {
  fetchAcceptedVacationsAndHolidays(); // Fetch events when the component is mounted
});
</script>

<style>
.sx-vue-calendar-wrapper {
  width: 100%;
  height: 800px;
  max-height: 90vh;
}
</style>
